import React, { useMemo, useState } from 'react';
import type { Language, Order } from '../types';
import type { Dict } from '../shared/i18n';
import { LOCALES } from '../shared/i18n';
import { Card, Empty } from '../components/shared';

interface HistoryPageProps {
  orders: Order[];
  lang: Language;
  t: Dict;
  onEditOrder: (order: Order) => void;
  onDeleteOrder: (id: string) => void;
}

const HistoryPage: React.FC<HistoryPageProps> = ({ orders, lang, t, onEditOrder, onDeleteOrder }) => {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const locale = lang === 'ar' ? 'ar-MA-u-nu-latn' : LOCALES[lang];
  const nf = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const dateFmt = new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric' });
  const timeFmt = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hour12: false });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter((o) =>
      o.clientName.toLowerCase().includes(q) ||
      o.dateString.includes(q) ||
      o.items.some((i) => i.productName.toLowerCase().includes(q)));
  }, [orders, query]);

  /** Day headers make a long list scannable across weeks. */
  const groups = useMemo(() => {
    const out: { date: string; ts: number; orders: Order[] }[] = [];
    for (const o of filtered) {
      const last = out[out.length - 1];
      if (last && last.date === o.dateString) last.orders.push(o);
      else out.push({ date: o.dateString, ts: o.timestamp, orders: [o] });
    }
    return out;
  }, [filtered]);

  if (orders.length === 0) return <Empty icon="📜" title={t.noOrders} />;

  return (
    <div className="space-y-3 qd-enter">
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t.search}
        aria-label={t.search}
        className="w-full bg-white border border-slate-200 p-3 rounded-xl font-medium min-h-[48px]"
      />

      {groups.length === 0 && <Empty icon="🔍" title={t.empty} />}

      {groups.map((group) => {
        const dayTotal = group.orders.reduce((s, o) => s + o.totalAmount, 0);
        return (
          <section key={group.date} className="space-y-2">
            <div className="flex justify-between items-center px-1 pt-3">
              <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">
                {dateFmt.format(group.ts)}
              </span>
              <span className="text-[10px] font-extrabold text-slate-400">
                {nf.format(dayTotal)} {t.currency}
              </span>
            </div>

            {group.orders.map((order) => {
              const isOpen = expanded === order.id;
              const unpaid = order.totalAmount - (order.paidAmount ?? order.totalAmount);
              return (
                <div key={order.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                  <button
                    onClick={() => setExpanded(isOpen ? null : order.id)}
                    aria-expanded={isOpen}
                    className="w-full p-4 flex justify-between items-center text-start"
                  >
                    <div className="min-w-0">
                      <p className="font-extrabold text-slate-900 truncate">{order.clientName}</p>
                      <p className="text-[10px] text-slate-400 font-bold mt-0.5">
                        {timeFmt.format(order.timestamp)} · {order.items.reduce((a, b) => a + b.quantity, 0)} {t.items}
                      </p>
                    </div>
                    <div className="text-end shrink-0 ms-3">
                      <p className="font-extrabold text-[#C1272D]">
                        {nf.format(order.totalAmount)} <span className="text-[9px]">{t.currency}</span>
                      </p>
                      {unpaid > 0.005 && (
                        <p className="text-[9px] font-extrabold text-amber-700 mt-0.5">
                          {t.outstanding} {nf.format(unpaid)}
                        </p>
                      )}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-4 bg-slate-50/60 border-t border-slate-100">
                      <div className="space-y-2 py-3">
                        {order.items.map((item, i) => (
                          <div key={`${item.productId}-${i}`} className="flex justify-between text-xs">
                            <span className="flex gap-2 min-w-0">
                              <span className="font-extrabold text-[#006233] w-6 shrink-0">{item.quantity}</span>
                              <span className="truncate">{item.productName}</span>
                            </span>
                            <span className="font-extrabold shrink-0 ms-2">{nf.format(item.subtotal)}</span>
                          </div>
                        ))}
                        {order.note && <p className="text-[11px] text-slate-400 italic pt-1">“{order.note}”</p>}
                      </div>
                      <div className="flex gap-2 pt-2 border-t border-slate-200">
                        <button
                          onClick={() => onEditOrder(order)}
                          className="flex-1 py-2.5 text-xs font-extrabold text-[#C1272D]"
                        >✏️ {t.edit}</button>
                        <button
                          onClick={() => {
                            if (confirm(`${order.clientName}\n${t.confirmDelete}`)) onDeleteOrder(order.id);
                          }}
                          className="flex-1 py-2.5 text-xs font-extrabold text-red-700"
                        >🗑️ {t.delete}</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        );
      })}
    </div>
  );
};

export default HistoryPage;
